import PropTypes from 'prop-types';
import { ChevronRight } from 'lucide-react';

import { List, ListItem, Separator } from './index.styles';

import Button from '../button';
import Icon from '../icon';
import Menu from '../menu';

const maxItems = 4;

function BreadcrumbsItem({ isLast, item, size, variant }) {
  const disabled = item.disabled || isLast;

  return (
    <ListItem>
      <Button
        as={(() => {
          if (item.as) {
            return item.as;
          }

          if (disabled) {
            return 'button';
          }

          if (item.href) {
            return 'a';
          }

          return undefined;
        })()}
        disabled={disabled}
        disabledAlt
        hasLabel
        href={item.href}
        onClick={item.onSelect}
        size={size}
        to={item.to}
        type="button"
        variant={variant}
      >
        {item.label}
      </Button>
    </ListItem>
  );
}

BreadcrumbsItem.propTypes = {
  isLast: PropTypes.bool,
  item: PropTypes.shape({
    as: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
    disabled: PropTypes.bool,
    href: PropTypes.string,
    label: PropTypes.node.isRequired,
    onSelect: PropTypes.func,
    to: PropTypes.string,
  }),
  size: PropTypes.oneOf(['xsmall', 'small']).isRequired,
  variant: PropTypes.oneOf(['secondary', 'tertiary']).isRequired,
};

function BreadcrumbsSeparatorItem() {
  return (
    <Separator>
      <Icon alt="" width={20} height={20} src={ChevronRight} inline />
    </Separator>
  );
}

function BreadcrumbsMenuItem({ items, size, variant }) {
  return (
    <ListItem>
      <Menu items={items} label="…" hasLabel={false} size={size} variant={variant} />
    </ListItem>
  );
}

BreadcrumbsMenuItem.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      as: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
      disabled: PropTypes.bool,
      href: PropTypes.string,
      label: PropTypes.string.isRequired,
      onSelect: PropTypes.func,
      to: PropTypes.string,
    }),
  ).isRequired,
  size: PropTypes.oneOf(['xsmall', 'small']).isRequired,
  variant: PropTypes.oneOf(['secondary', 'tertiary']).isRequired,
};

/**
 * Renders breadcrumbs. If there are too many items, creates a submenu containing the items from
 * the middle.
 */
export default function Breadcrumbs({ items = [], size = 'small', variant = 'tertiary' }) {
  const hasTooManyItems = items.length > maxItems;

  return (
    <List>
      {items.reduce((prev, item, index) => {
        const key = index;

        const el = (
          <BreadcrumbsItem
            key={key}
            isLast={index + 1 === items.length}
            item={item}
            size={size}
            variant={variant}
          />
        );

        if (index > 0) {
          const sep = <BreadcrumbsSeparatorItem key={`${key}_sep`} />;

          if (hasTooManyItems && index > 0 && index - 1 < items.length - (maxItems - 1)) {
            if (index === 1) {
              const menu = (
                <BreadcrumbsMenuItem
                  key={key}
                  items={items.slice(1, -(maxItems - 2))}
                  size={size}
                  variant={variant}
                />
              );

              return [...prev, sep, menu];
            }

            return [...prev];
          }

          return [...prev, sep, el];
        }

        return [...prev, el];
      }, [])}
    </List>
  );
}

Breadcrumbs.propTypes = {
  /**
   * Breadcrumbs items.
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      as: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
      disabled: PropTypes.bool,
      href: PropTypes.string,
      label: PropTypes.string.isRequired,
      onSelect: PropTypes.func,
      to: PropTypes.string,
    }),
  ).isRequired,
  /**
   * Breadcrumbs size.
   */
  size: PropTypes.oneOf(['xsmall', 'small']),
  /**
   * Breadcrumbs variant.
   */
  variant: PropTypes.oneOf(['secondary', 'tertiary']),
};
